import { createRef, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';

import { Avatar, Box, Grid, Typography, useMediaQuery } from '@material-ui/core';
import { useTheme, withStyles } from '@material-ui/core/styles';

import { LazyContent, Link, LinkButton, Rating } from 'evee-ui-common/components/base';
import { vehicle } from 'evee-ui-common/types';

import useDebouncedResizeObserver from 'common/hooks/useDebouncedResizeObserver';

import styles from './styles';

const Review = ({ review, classes }) => {
  const [seeFullReview, setSeeFullReview] = useState(false);
  const [ellipsisActive, setEllipsisActive] = useState(false);
  const reviewClsx = clsx(classes.review, !seeFullReview && classes.clampedReview);
  const { review: reviewText, photo, customer, name, nickname, date, rating } = review;
  const accountHRef = `/account/${customer}`;

  const reviewRef = createRef();
  const { width } = useDebouncedResizeObserver(10, reviewRef);

  useEffect(() => {
    if (reviewRef.current) {
      const reviewOverflow = reviewRef.current.offsetHeight < reviewRef.current.scrollHeight;
      setEllipsisActive(reviewOverflow);
    }
  }, [width]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Grid container spacing={isMobile ? 1 : 4} style={{ marginBottom: '5em' }}>
      <Grid item xs={12}>
        <Box
          className={classes.customerWrapper}
          display="flex"
          flexDirection="row"
          alignItems="center"
          width="100%"
        >
          <Box className={classes.avatarWrapper}>
            <Link href={accountHRef} underline="none">
              <LazyContent>
                <Avatar className={classes.avatar} src={photo}>
                  {nickname}
                </Avatar>
              </LazyContent>
            </Link>
          </Box>
          <Box display="flex" flexDirection="column">
            <Link component={NavLink} to={accountHRef} underline="none">
              <Typography variant="h6">{name}</Typography>
            </Link>
            <Typography variant="body2">{moment(date).format('ll')}</Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" flexDirection="column" width="100%">
          <Box>
            <Typography ref={reviewRef} variant="body1" className={reviewClsx}>
              {reviewText}
            </Typography>

            {ellipsisActive && !seeFullReview && (
              <LinkButton
                text="Read more"
                style={{ marginBottom: theme.spacing(1), padding: 0 }}
                onClick={() => setSeeFullReview(true)}
              />
            )}

            {ellipsisActive && seeFullReview && (
              <LinkButton
                text="Read less"
                style={{ marginBottom: theme.spacing(1), padding: 0 }}
                onClick={() => setSeeFullReview(false)}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center">
            <Rating readOnly value={rating} />
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {Number(rating, 1).toFixed(1)}
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

Review.propTypes = {
  review: vehicle.review,
  classes: PropTypes.shape({
    avatar: PropTypes.string,
    accountLink: PropTypes.string,
    customerWrapper: PropTypes.string,
    avatarWrapper: PropTypes.string,
    review: PropTypes.string,
    clampedReview: PropTypes.string,
  }).isRequired,
};

Review.defaultProps = {
  review: {},
};

export default withStyles(styles)(Review);
